import { useEffect, useMemo, useState } from 'react';
import useApi from '../api';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface FunnelStepResult {
  id: string;
  label: string;
  count: number;
  rate_from_top: number;
  rate_from_previous: number;
}

interface ConversionFunnelProps {
  timeRange: string;
}

const FUNNEL_STEPS = [
  { id: 'visitors', label: 'Visitors', toggleable: false },
  { id: 'session_started', label: 'Session Started', toggleable: true },
  { id: 'lead_created', label: 'Lead Created', toggleable: true },
  { id: 'calendar_shown', label: 'Calendar Shown', toggleable: true },
  { id: 'meeting_or_escalated', label: 'Meeting Booked or Escalated', toggleable: true },
] as const;

const DEFAULT_SELECTED_STEPS = FUNNEL_STEPS.map((step) => step.id);

const VIEWBOX_WIDTH = 1000;
const SEGMENT_HEIGHT = 110;
const MAX_SEGMENT_WIDTH = 940;
const MIN_SEGMENT_WIDTH = 160;
const CENTER_X = VIEWBOX_WIDTH / 2;

// Match the dashboard's area chart aesthetic: a single clean blue hue that
// fades from a saturated top to a near-transparent bottom. Hardcoded so the
// fade is consistently visible across light and dark themes.
const FUNNEL_COLOR = 'hsl(217, 91%, 60%)';

const formatNumber = (n: number) => n.toLocaleString();

const ConversionFunnel = ({ timeRange }: ConversionFunnelProps) => {
  const { get } = useApi();
  const [selectedSteps, setSelectedSteps] = useState<string[]>(DEFAULT_SELECTED_STEPS);
  const [funnelData, setFunnelData] = useState<FunnelStepResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const orderedSelectedSteps = useMemo(
    () => FUNNEL_STEPS.filter((step) => selectedSteps.includes(step.id)).map((step) => step.id),
    [selectedSteps]
  );

  const orderedKey = orderedSelectedSteps.join(',');

  useEffect(() => {
    const fetchFunnelData = async () => {
      setIsLoading(true);
      try {
        const response = await get(
          `/advanced/conversion-funnel?time_range=${timeRange}&steps=${orderedKey}`
        );
        setFunnelData(response.data.steps || []);
      } catch (error) {
        console.error('Error fetching conversion funnel data:', error);
        setFunnelData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFunnelData();
  }, [timeRange, orderedKey]);

  const toggleStep = (stepId: string, checked: boolean) => {
    setSelectedSteps((prev) => {
      if (checked) {
        return [...prev, stepId];
      }
      return prev.filter((id) => id !== stepId);
    });
  };

  const topCount = funnelData[0]?.count ?? 0;

  const segments = useMemo(() => {
    if (funnelData.length === 0 || topCount === 0) return [];

    const widthFor = (r: number) =>
      MIN_SEGMENT_WIDTH + (MAX_SEGMENT_WIDTH - MIN_SEGMENT_WIDTH) * Math.max(Math.min(r, 1), 0);

    return funnelData.map((step, index) => {
      const ratio = step.count / topCount;
      const previousRatio = index > 0 ? funnelData[index - 1].count / topCount : 1;

      const topWidth = widthFor(previousRatio);
      const bottomWidth = widthFor(ratio);

      const yTop = index * SEGMENT_HEIGHT;
      const yBottom = yTop + SEGMENT_HEIGHT;
      const yCenter = yTop + SEGMENT_HEIGHT / 2;

      return {
        step,
        index,
        yTop,
        yBottom,
        yCenter,
        topWidth,
        bottomWidth,
        leftTop: CENTER_X - topWidth / 2,
        rightTop: CENTER_X + topWidth / 2,
        leftBottom: CENTER_X - bottomWidth / 2,
        rightBottom: CENTER_X + bottomWidth / 2,
      };
    });
  }, [funnelData, topCount]);

  // Build a single continuous polygon path for the entire funnel so the
  // outline reads as one convex shape (straight kinks at each step boundary
  // instead of a stack of S-curves).
  const funnelPath = useMemo(() => {
    if (segments.length === 0) return '';

    const first = segments[0];
    const last = segments[segments.length - 1];
    const cmds: string[] = [];

    cmds.push(`M ${first.leftTop} ${first.yTop}`);
    cmds.push(`L ${first.rightTop} ${first.yTop}`);
    segments.forEach((seg) => {
      cmds.push(`L ${seg.rightBottom} ${seg.yBottom}`);
    });
    cmds.push(`L ${last.leftBottom} ${last.yBottom}`);
    for (let i = segments.length - 1; i >= 0; i--) {
      cmds.push(`L ${segments[i].leftTop} ${segments[i].yTop}`);
    }
    cmds.push('Z');
    return cmds.join(' ');
  }, [segments]);

  const totalHeight = segments.length > 0 ? segments.length * SEGMENT_HEIGHT : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversion Funnel</CardTitle>
        <CardDescription>
          Cumulative visitor progression through key journey milestones. Each step counts visitors
          who completed all previously selected steps and this one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Show steps
          </span>
          {FUNNEL_STEPS.filter((step) => step.toggleable).map((step) => (
            <div key={step.id} className="flex items-center gap-2">
              <Checkbox
                id={`funnel-step-${step.id}`}
                checked={selectedSteps.includes(step.id)}
                onCheckedChange={(checked) => toggleStep(step.id, checked === true)}
              />
              <Label
                htmlFor={`funnel-step-${step.id}`}
                className="text-sm font-normal cursor-pointer"
              >
                {step.label}
              </Label>
            </div>
          ))}
        </div>

        {isLoading ? (
          <div className="h-[440px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : segments.length === 0 || topCount === 0 ? (
          <div className="h-[440px] flex items-center justify-center text-muted-foreground">
            No visitor data for this time range
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-8 items-start">
            {/* Single convex funnel */}
            <div className="w-full">
              <svg
                viewBox={`0 0 ${VIEWBOX_WIDTH} ${totalHeight}`}
                preserveAspectRatio="xMidYMid meet"
                className="w-full h-auto overflow-visible"
              >
                <defs>
                  {/* Multi-stop gradient: each section naturally lands on a
                      slightly different shade so they're distinguishable
                      while still blending smoothly. */}
                  <linearGradient id="funnel-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={FUNNEL_COLOR} stopOpacity={0.55} />
                    <stop offset="35%" stopColor={FUNNEL_COLOR} stopOpacity={0.35} />
                    <stop offset="70%" stopColor={FUNNEL_COLOR} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={FUNNEL_COLOR} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="funnel-stroke" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={FUNNEL_COLOR} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={FUNNEL_COLOR} stopOpacity={0.15} />
                  </linearGradient>
                  <linearGradient id="funnel-divider" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={FUNNEL_COLOR} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={FUNNEL_COLOR} stopOpacity={0.15} />
                  </linearGradient>
                </defs>

                <path
                  d={funnelPath}
                  fill="url(#funnel-fill)"
                  stroke="url(#funnel-stroke)"
                  strokeWidth={1.25}
                  strokeLinejoin="round"
                  className="transition-all duration-500 ease-out"
                />

                {/* Subtle highlight on the funnel's top edge for a "lit from
                    above" feel that gives the shape some dimension. */}
                {segments[0] && (
                  <line
                    x1={segments[0].leftTop + 1}
                    y1={segments[0].yTop + 0.75}
                    x2={segments[0].rightTop - 1}
                    y2={segments[0].yTop + 0.75}
                    stroke="white"
                    strokeOpacity={0.22}
                    strokeWidth={1}
                  />
                )}

                {/* Clean section dividers: solid 1px lines that fade with the
                    overall gradient depth and extend just past the funnel
                    edge with small tick marks for a refined feel. */}
                {segments.slice(0, -1).map((seg, i) => {
                  const depth = (i + 1) / segments.length;
                  const lineOpacity = 0.55 - depth * 0.4;
                  const tickLength = 8;
                  return (
                    <g key={`div-${seg.step.id}`}>
                      <line
                        x1={seg.leftBottom}
                        y1={seg.yBottom}
                        x2={seg.rightBottom}
                        y2={seg.yBottom}
                        stroke="url(#funnel-divider)"
                        strokeOpacity={lineOpacity}
                        strokeWidth={1}
                      />
                      <line
                        x1={seg.leftBottom - tickLength}
                        y1={seg.yBottom}
                        x2={seg.leftBottom}
                        y2={seg.yBottom}
                        stroke={FUNNEL_COLOR}
                        strokeOpacity={lineOpacity * 0.6}
                        strokeWidth={1}
                      />
                      <line
                        x1={seg.rightBottom}
                        y1={seg.yBottom}
                        x2={seg.rightBottom + tickLength}
                        y2={seg.yBottom}
                        stroke={FUNNEL_COLOR}
                        strokeOpacity={lineOpacity * 0.6}
                        strokeWidth={1}
                      />
                    </g>
                  );
                })}

                {segments.map((seg) => {
                  const innerWidth = Math.min(seg.topWidth, seg.bottomWidth);
                  const fitsLabel = innerWidth > 200;
                  const fitsCount = innerWidth > 120;

                  return (
                    <g key={seg.step.id}>
                      {fitsLabel && (
                        <text
                          x={CENTER_X}
                          y={seg.yCenter - 8}
                          textAnchor="middle"
                          className="fill-foreground/75"
                          fontSize="13"
                          fontWeight="500"
                          style={{ letterSpacing: '0.4px', textTransform: 'uppercase' }}
                        >
                          {seg.step.label}
                        </text>
                      )}
                      {fitsCount && (
                        <text
                          x={CENTER_X}
                          y={seg.yCenter + (fitsLabel ? 22 : 6)}
                          textAnchor="middle"
                          className="fill-foreground"
                          fontSize="30"
                          fontWeight="600"
                        >
                          {formatNumber(seg.step.count)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Stats panel */}
            <div className="flex flex-col gap-2">
              {funnelData.map((step, index) => {
                const previousCount = index > 0 ? funnelData[index - 1].count : step.count;
                const dropOff = index > 0 ? Math.max(previousCount - step.count, 0) : 0;
                return (
                  <div
                    key={step.id}
                    className="rounded-lg border bg-card/50 px-4 py-3 transition-colors hover:bg-card"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground">{step.label}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {step.rate_from_top}%
                      </span>
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-xl font-semibold tabular-nums">
                        {formatNumber(step.count)}
                      </span>
                      {index > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {step.rate_from_previous}% from previous
                          {dropOff > 0 && (
                            <span className="ml-2 text-red-500/80 tabular-nums">
                              −{formatNumber(dropOff)}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversionFunnel;
